import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Bell, BellRing, Calendar, Check, Send, Radio } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";

const fmtWhen = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  return d.toLocaleString("en-US", {
    weekday: "long",
    month: "long",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
};

export const UpcomingBroadcasts = ({ variant = "feed", onStartNow }) => {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [busy, setBusy] = useState(null);

  const load = async () => {
    try {
      const { data } = await api.get("/live/scheduled");
      setItems(data || []);
    } catch {
      setItems([]);
    }
  };

  useEffect(() => { load(); }, []);

  const toggleRemind = async (id) => {
    if (!user) {
      toast("Sign in to be reminded.");
      return;
    }
    setBusy(id);
    try {
      const { data } = await api.post(`/live/scheduled/${id}/remind-me`);
      setItems((prev) => prev.map((s) => (s.id === id ? { ...s, remind_me: data.remind_me } : s)));
      toast.success(data.remind_me ? "We'll let you know." : "Reminder turned off.");
    } catch {
      toast.error("Could not update reminder.");
    } finally {
      setBusy(null);
    }
  };

  if (!items.length) return null;

  // Feed variant: a single soft card pinned at the top
  if (variant === "feed") {
    const next = items[0];
    return (
      <div
        data-testid="upcoming-broadcast-feed"
        style={{
          background: "var(--surface)",
          border: "1px solid var(--brand-accent)",
          padding: "22px 28px",
          marginBottom: 28,
          display: "flex",
          gap: 20,
          alignItems: "center",
          flexWrap: "wrap",
        }}
      >
        <div style={{ flex: 1, minWidth: 240 }}>
          <div className="overline" style={{ color: "var(--brand-accent)", display: "flex", alignItems: "center", gap: 8 }}>
            <Calendar size={12} strokeWidth={1.6} /> Coming up at the salon
          </div>
          <Link to="/live" style={{ textDecoration: "none", color: "var(--text-primary)" }}>
            <h3 className="font-serif" style={{ fontSize: 26, marginTop: 6, fontWeight: 500 }}>
              {next.title}
            </h3>
          </Link>
          <p className="editorial-body" style={{ fontSize: 13, color: "var(--text-secondary)", marginTop: 4 }}>
            {fmtWhen(next.scheduled_at)}
            {next.editor?.display_name ? ` · with ${next.editor.display_name}` : ""}
          </p>
        </div>
        <button
          type="button"
          onClick={() => toggleRemind(next.id)}
          disabled={busy === next.id}
          data-testid={`feed-remind-${next.id}`}
          className={next.remind_me ? "btn-secondary" : "btn-primary"}
          style={{ display: "inline-flex", alignItems: "center", gap: 8 }}
        >
          {next.remind_me ? <BellRing size={14} /> : <Bell size={14} />}
          {next.remind_me ? "Reminding you" : "Remind me"}
        </button>
      </div>
    );
  }

  // Live page variant: full grid of upcoming items with description + cover
  return (
    <div data-testid="upcoming-broadcast-live" style={{ marginBottom: 48 }}>
      <div className="overline" style={{ color: "var(--brand-accent)", marginBottom: 14, display: "flex", alignItems: "center", gap: 8 }}>
        <Calendar size={12} strokeWidth={1.6} /> Coming up
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(320px, 1fr))", gap: 24 }}>
        {items.map((s) => {
          const isOwner = user?.is_editor && user?.id === s.editor_id;
          // "Start now" is offered from 30 minutes before scheduled_at until
          // it gets auto-archived — gives the editor a one-click way to open
          // the Go Live modal with the schedule's title/room pre-filled.
          let showStartNow = false;
          if (isOwner && onStartNow && s.scheduled_at) {
            const t = new Date(s.scheduled_at).getTime();
            const now = Date.now();
            showStartNow = now >= t - 30 * 60 * 1000;
          }
          return (
            <div
              key={s.id}
              data-testid={`upcoming-card-${s.id}`}
              style={{
                background: "var(--surface)",
                border: "1px solid var(--border-default)",
                padding: 22,
                display: "flex",
                flexDirection: "column",
                gap: 12,
              }}
            >
              {s.cover_image_url && (
                <img
                  loading="lazy" decoding="async" src={s.cover_image_url}
                  alt=""
                  style={{ width: "100%", aspectRatio: "16 / 9", objectFit: "cover", border: "1px solid var(--border-default)" }}
                />
              )}
              <div className="overline" style={{ color: "var(--brand-accent)" }}>
                {s.section || "Live"} · {fmtWhen(s.scheduled_at)}
              </div>
              <h3 className="font-serif" style={{ fontSize: 24, fontWeight: 500, lineHeight: 1.25, margin: 0 }}>
                {s.title}
              </h3>
              {s.description && (
                <p className="editorial-body" style={{ fontSize: 14, color: "var(--text-secondary)", lineHeight: 1.55, margin: 0 }}>
                  {s.description}
                </p>
              )}
              <p className="overline" style={{ color: "var(--text-muted)", fontSize: 10 }}>
                {s.reminder_count} {s.reminder_count === 1 ? "person reminded" : "people reminded"}
              </p>
              {isOwner && s.reminders_sent && (
                <div
                  data-testid={`reminders-sent-${s.id}`}
                  className="overline"
                  style={{
                    display: "flex", gap: 12, alignItems: "center",
                    fontSize: 10, color: "var(--text-muted)", marginTop: -4,
                  }}
                  title="These indicators are automatic — a check appears once the reminder push has been sent to members who opted in."
                >
                  <span>Push status (auto):</span>
                  {[
                    { key: "day", label: "24h" },
                    { key: "hour", label: "1h" },
                    { key: "live", label: "Live" },
                  ].map(({ key, label }) => {
                    const sent = s.reminders_sent[key];
                    return (
                      <span
                        key={key}
                        title={sent ? `${label} reminder sent` : `${label} reminder not yet sent`}
                        style={{
                          display: "inline-flex", alignItems: "center", gap: 4,
                          color: sent ? "var(--brand-accent)" : "var(--text-muted)",
                          opacity: sent ? 1 : 0.55,
                        }}
                      >
                        {sent ? <Check size={11} strokeWidth={2} /> : <span style={{ display: "inline-block", width: 8, height: 8, border: "1px solid currentColor", borderRadius: "50%" }} />}
                        {label}
                      </span>
                    );
                  })}
                </div>
              )}
              <div style={{ display: "flex", gap: 10, marginTop: 4, flexWrap: "wrap" }}>
                {showStartNow && (
                  <button
                    type="button"
                    onClick={() => onStartNow(s)}
                    data-testid={`start-now-${s.id}`}
                    className="btn-primary"
                    title="Start this broadcast now — opens Go Live with title & room pre-filled"
                    style={{ display: "inline-flex", alignItems: "center", gap: 8, background: "var(--brand-accent)" }}
                  >
                    <Radio size={14} /> Start broadcast
                  </button>
                )}
                <button
                  type="button"
                  onClick={() => toggleRemind(s.id)}
                  disabled={busy === s.id}
                  data-testid={`live-remind-${s.id}`}
                  className={s.remind_me ? "btn-secondary" : "btn-primary"}
                  style={{ display: "inline-flex", alignItems: "center", gap: 8 }}
                >
                  {s.remind_me ? <BellRing size={14} /> : <Bell size={14} />}
                  {s.remind_me ? "Reminding you" : "Remind me"}
                </button>
                {isOwner && (
                  <>
                    <button
                      type="button"
                      onClick={async () => {
                        try {
                          const { data } = await api.post(`/live/scheduled/${s.id}/test-push`);
                          if (data.ok) {
                            toast.success(`Test push sent to ${data.device_count} of your device${data.device_count === 1 ? "" : "s"}.`);
                          } else {
                            toast(data.detail || "No devices subscribed.");
                          }
                        } catch (err) {
                          toast.error(err.response?.data?.detail || "Push failed.");
                        }
                      }}
                      data-testid={`test-push-${s.id}`}
                      className="btn-secondary"
                      title="Send a test push to your own devices only"
                      style={{ display: "inline-flex", alignItems: "center", gap: 8 }}
                    >
                      <Send size={13} strokeWidth={1.6} />
                      Test push
                    </button>
                    <button
                      type="button"
                      onClick={async () => {
                        // eslint-disable-next-line no-restricted-globals, no-alert
                        if (!window.confirm("Cancel this upcoming broadcast?")) return;
                        try {
                          await api.delete(`/live/scheduled/${s.id}`);
                          setItems((prev) => prev.filter((x) => x.id !== s.id));
                          toast.success("Cancelled.");
                        } catch {
                          toast.error("Could not cancel.");
                        }
                      }}
                      data-testid={`cancel-schedule-${s.id}`}
                      className="btn-secondary"
                      style={{ color: "var(--brand-accent)" }}
                    >
                      Cancel
                    </button>
                  </>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default UpcomingBroadcasts;
